import { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { FormField, FormToggle } from '@/shared/components'
import { ProgrammeActionRow } from './programme-action-row'
import { useActionsActifs } from '@/features/actions/hooks/use-actions'
import type { Programme } from '@/shared/types/admin'
import type { TypeProgramme } from '@/shared/types/common'

const programmeActionSchema = z.object({
  id: z.number().optional(),
  actionId: z.number().min(1, "Sélectionnez une action"),
  jourDeclenchement: z.number(),
})

const schema = z.object({
  libelle: z.string().min(1, 'Le libellé est obligatoire'),
  type: z.enum(['FORMATEUR', 'ETUDIANT']),
  actif: z.boolean(),
  actions: z.array(programmeActionSchema),
})

type FormValues = z.infer<typeof schema>

interface ProgrammeFormProps {
  programme?: Programme
  onSubmit: (data: {
    libelle: string
    type: TypeProgramme
    actif: boolean
    actions: Array<{ id?: number; actionId: number; jourDeclenchement: number }>
  }) => void
  isPending: boolean
}

export function ProgrammeForm({ programme, onSubmit, isPending }: ProgrammeFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: actionsDisponibles = [] } = useActionsActifs()

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { libelle: '', type: 'FORMATEUR', actif: true, actions: [] },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'actions',
  })

  useEffect(() => {
    if (programme) {
      setValue('libelle', programme.libelle)
      setValue('type', programme.type)
      setValue('actif', programme.actif)
      if (programme.actions?.length) {
        setValue('actions', programme.actions.map((pa) => ({
          id: pa.id,
          actionId: pa.action?.id || pa.actionId || 0,
          jourDeclenchement: pa.jourDeclenchement,
        })))
      }
    }
  }, [programme, setValue])

  const handleFormSubmit = (values: FormValues) => {
    onSubmit({
      libelle: values.libelle.trim(),
      type: values.type as TypeProgramme,
      actif: values.actif,
      actions: values.actions.map((a) => ({
        ...(a.id ? { id: a.id } : {}),
        actionId: a.actionId,
        jourDeclenchement: a.jourDeclenchement,
      })),
    })
  }

  const inputClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="mx-auto max-w-lg space-y-6">
      <div className="rounded-xl border border-neutral-200 bg-white p-6 space-y-5">
        <h2 className="text-base font-semibold text-neutral-900">
          {t('programmes.formInfo', 'Informations du programme')}
        </h2>

        <FormField
          label={t('programmes.formLabel', 'Libellé du programme')}
          error={errors.libelle?.message}
          required
        >
          <input {...register('libelle')} className={inputClass} />
        </FormField>

        <FormField label={t('programmes.formType', 'Type de programme')} required>
          <select {...register('type')} className={inputClass}>
            <option value="FORMATEUR">{t('programmes.typeFormateur', 'Formateur')}</option>
            <option value="ETUDIANT">{t('programmes.typeEtudiant', 'Étudiant')}</option>
          </select>
        </FormField>

        <FormToggle
          label={t('programmes.formActive', 'Programme actif')}
          checked={watch('actif')}
          onChange={(v) => setValue('actif', v)}
        />
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-neutral-900">
            {t('programmes.formActions', 'Actions du programme')}
          </h2>
          <button
            type="button"
            onClick={() => append({ actionId: 0, jourDeclenchement: 0 })}
            className="flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('programmes.addAction', 'Ajouter une action')}
          </button>
        </div>

        {fields.length > 0 && (
          <div className="flex items-center gap-3 px-3 text-xs font-medium text-neutral-500">
            <div className="flex-1">Action</div>
            <div className="w-24">Jour</div>
            <div className="w-8" />
          </div>
        )}

        {fields.length === 0 && (
          <p className="py-4 text-center text-sm text-neutral-400">
            {t('programmes.noActions', 'Aucune action ajoutée.')}
          </p>
        )}

        <div className="space-y-2">
          {fields.map((field, index) => (
            <ProgrammeActionRow
              key={field.id}
              index={index}
              register={register}
              setValue={setValue}
              actionsDisponibles={actionsDisponibles}
              onRemove={() => remove(index)}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/programmes')}
          className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          {t('common.cancel', 'Annuler')}
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending
            ? t('common.saving', 'Enregistrement...')
            : programme
              ? t('common.save', 'Enregistrer')
              : t('programmes.create', 'Créer le programme')}
        </button>
      </div>
    </form>
  )
}
